import { useEffect, useState } from "react";
import { bubbleSort, heapSort, mergeSort, quickSort } from "./algorithms";
import AlgorithmInfo from "../components/AlgorithmInfo";
import "./sorting.css";
export default function Sorting() {
  interface Bar {
    value: number;
    color: string;
  }
  enum Algorithms {
    quickSort = "Quick Sort",
    mergeSort = "Merge Sort",
    heapSort = "Heap Sort",
    bubbleSort = "Bubble Sort",
  }
  const [algo, setalgo]: any = useState(Algorithms.quickSort);
  const [array, setarray] = useState<Bar[]>([]);
  const [change, setchange]: any = useState(0.8);
  const [isRunning, setIsRunning] = useState(false);
  const [speed, setSpeed] = useState(50);
  const [arraySize, setArraySize] = useState(50);
  const [stats, setStats] = useState({
    comparisons: 0,
    swaps: 0,
    timeElapsed: 0
  });
  useEffect(() => {
    resetArray();
  }, []);
  function resetArray() {
    let temp: Bar[] = [];
    for (let i = 0; i < arraySize; i++) {
      let min = 20,
        max = 500;
      temp.push({
        value: Math.floor(Math.random() * (max - min + 1) + min),
        color: "#667eea",
      });
    }
    setarray(temp);
    setStats({ comparisons: 0, swaps: 0, timeElapsed: 0 });
  }
  function handleAlgoChange(e: any) {
    setalgo(e.currentTarget.value);
    resetArray();
  }
  async function sort() {
    if (isRunning) return;
    
    setIsRunning(true);
    setStats(prev => ({ ...prev, comparisons: 0, swaps: 0, timeElapsed: 0 }));
    
    const startTime = Date.now();
    let arr = [...array];
    
    // Create a function to update the array state in real-time
    const updateArray = (newArr: Bar[], comparing?: number[], swapping?: number[]) => {
      // Add visual indicators for comparison and swapping
      const updatedArr = newArr.map((bar, index) => {
        let color = bar.color;
        if (comparing && comparing.includes(index)) {
          color = "#ff6b6b"; // Red for comparing
        } else if (swapping && swapping.includes(index)) {
          color = "#feca57"; // Yellow for swapping
        } else {
          color = "#667eea"; // Default color
        }
        return { ...bar, color };
      });
      
      setarray([...updatedArr]);
      setchange(Math.random());
    };
    
    console.log("before: ", arr);
    switch (algo) {
      case Algorithms.quickSort:
        await (quickSort as any)(arr, 0, arr.length - 1, updateArray, speed, (stats: { comparisons?: number; swaps?: number }) => {
          setStats(prev => ({ 
            ...prev, 
            comparisons: prev.comparisons + (stats.comparisons || 0),
            swaps: prev.swaps + (stats.swaps || 0)
          }));
        });
        break;
      case Algorithms.mergeSort:
        await (mergeSort as any)(arr, 0, arr.length - 1, updateArray, speed, (stats: { comparisons?: number; swaps?: number }) => {
          setStats(prev => ({ 
            ...prev, 
            comparisons: prev.comparisons + (stats.comparisons || 0),
            swaps: prev.swaps + (stats.swaps || 0)
          }));
        });
        break;
      case Algorithms.bubbleSort:
        await (bubbleSort as any)(arr, updateArray, speed, (stats: { comparisons?: number; swaps?: number }) => {
          setStats(prev => ({ 
            ...prev, 
            comparisons: prev.comparisons + (stats.comparisons || 0),
            swaps: prev.swaps + (stats.swaps || 0)
          }));
        });
        break;
      case Algorithms.heapSort:
        await (heapSort as any)(arr, updateArray, speed, (stats: { comparisons?: number; swaps?: number }) => {
          setStats(prev => ({ 
            ...prev, 
            comparisons: prev.comparisons + (stats.comparisons || 0),
            swaps: prev.swaps + (stats.swaps || 0)
          }));
        });
        break;
      default:
        break;
    }
    
    const endTime = Date.now();
    setStats(prev => ({ ...prev, timeElapsed: endTime - startTime }));
    setIsRunning(false);
    console.log("after: ", arr);
  }

  return (
    <div>
      <div className="control-panel">
        <div className="control-group">
          <label className="control-label">Algorithm:</label>
          <select className="control-select" onChange={handleAlgoChange} value={algo}>
            <option value={Algorithms.quickSort}>{Algorithms.quickSort}</option>
            <option value={Algorithms.mergeSort}>{Algorithms.mergeSort}</option>
            <option value={Algorithms.heapSort}>{Algorithms.heapSort}</option>
            <option value={Algorithms.bubbleSort}>{Algorithms.bubbleSort}</option>
          </select>
        </div>
        
        <div className="control-group">
          <label className="control-label">Array Size:</label>
          <div className="speed-control">
            <input 
              type="range" 
              min="10" 
              max="100" 
              value={arraySize} 
              onChange={(e) => {
                setArraySize(Number(e.target.value));
                resetArray();
              }}
              className="speed-slider"
              disabled={isRunning}
            />
            <span>{arraySize}</span>
          </div>
        </div>
        
        <div className="control-group">
          <label className="control-label">Speed:</label>
          <div className="speed-control">
            <input 
              type="range" 
              min="10" 
              max="200" 
              value={speed} 
              onChange={(e) => setSpeed(Number(e.target.value))}
              className="speed-slider"
            />
            <span>{speed}ms</span>
          </div>
        </div>
        
        <div className="control-group">
          <button className="control-button secondary" onClick={resetArray} disabled={isRunning}>
            Reset Array
          </button>
          <button 
            className="control-button primary" 
            onClick={sort}
            disabled={isRunning}
          >
            {isRunning ? 'Sorting...' : 'Start Sort'}
          </button>
        </div>
      </div>
      
      <div className="stats-panel">
        <div className="stat-item">
          <span className="stat-value">{stats.comparisons}</span>
          <span className="stat-label">Comparisons</span>
        </div>
        <div className="stat-item">
          <span className="stat-value">{stats.swaps}</span>
          <span className="stat-label">Swaps</span>
        </div>
        <div className="stat-item">
          <span className="stat-value">{stats.timeElapsed}ms</span>
          <span className="stat-label">Time Elapsed</span>
        </div>
      </div>
      
      <div className="sorting-container">
        {/* <div className="sorting-instructions">
          <p>Watch the bars sort in real-time • Different colors show comparison and swap operations</p>
        </div> */}
        <div className="sorting-visualization">
          <div className="bars-container">
            {array.map((bar: Bar, idx) => (
              <div
                className="bar"
                key={idx}
                style={{
                  height: `${(bar.value / 500) * 100}%`,
                  backgroundColor: bar.color,
                  width: `${100 / arraySize}%`,
                  transition: isRunning ? 'none' : 'all 0.3s ease',
                }}
              />
            ))}
          </div>
        </div>
      </div>
      
      <AlgorithmInfo algorithm={algo} type="sorting" />
    </div>
  );
}
